import React, { useEffect } from 'react'
import { useState } from 'react'

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { FieldHookConfig, getIn, useField } from 'formik'
import NumberFormat from 'react-number-format'

import LoadingIcon from '../LoadingIcon'
import { useGetSsnUnmaskedQuery } from '@/services/applicationProfile'
import { useGetSsnUnmaskedPartyQuery } from '@/services/v2-application/applicationParties'
import { ApplicationParty, Profile } from '@/types/unlock'

interface CustomProps {
  name: string
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  disabled?: boolean
  maskedFormat: string
  profile?: Profile
  style?: 'minimal' | 'default'
  label?: string
  labelFlag?: React.ReactNode
  afterOnChange?: () => void
  showError?: boolean
  errors?: any
  touched?: any
}

// @TODO - Remove `any` by defining what props we should pass to <NumberFormat />
type CustomNumberFormatProps = any

type MaskedSSNNUmberFieldProps = CustomProps & CustomNumberFormatProps & FieldHookConfig<string>

/**
 * @deprecated use Formik > Field and customize at the feature level
 * @see useForm() hook
 */
const MaskedSSNNUmberField = (props: MaskedSSNNUmberFieldProps) => {
  const {
    label,
    labelFlag,
    setFieldValue,
    name,
    profile,
    style = 'minimal',
    afterOnChange,
    showError = false,
    errors,
    touched,
  } = props
  const [field, , helpers] = useField(props)
  const [isMasked, setIsMasked] = useState(true)
  const [rawValue, setRawValue] = useState(undefined)
  const [skipFetchUnmasked, setSkipFetchUnmasked] = useState(true)
  const { data: applicantSsnUnmasked, isLoading } = useGetSsnUnmaskedQuery(profile?.uuid, {
    skip: skipFetchUnmasked,
  })

  const fieldClasses = style === 'minimal' ? 'minimal-input' : 'classic-input'
  const maskedFormat = props.maskedFormat.replaceAll('-', '')

  useEffect(() => {
    if (
      profile &&
      (rawValue ?? applicantSsnUnmasked?.social_security_number ?? false) &&
      rawValue !== applicantSsnUnmasked?.social_security_number
    ) {
      setRawValue(applicantSsnUnmasked?.social_security_number)
    }
  }, [profile, applicantSsnUnmasked, rawValue])

  const getValue = () => {
    let value = ''

    if (rawValue === undefined) {
      return value
    }

    const _rawValue = rawValue?.replaceAll('-', '')
    for (let i = 0; i < field?.value?.length && i < maskedFormat?.length; i++) {
      if (isMasked && maskedFormat[i] === '*') {
        value += '*'
      } else {
        value += _rawValue[i]
      }
    }

    return value
  }

  useEffect(() => {
    if (rawValue === undefined) {
      return
    }

    const newValue = getValue()
    setFieldValue(name, newValue, true)
  }, [rawValue, isMasked, setFieldValue, name])

  const fetchUnmaskedValue = () => {
    if (skipFetchUnmasked && profile) setSkipFetchUnmasked(false)
  }

  const toggleComponent = () => {
    if (props.disabled) {
      return
    }

    fetchUnmaskedValue()
    setIsMasked(!isMasked)
  }

  const displayEyeIcon = () => {
    if (isMasked) {
      return (
        <EyeIcon
          onClick={toggleComponent}
          className='cursor-pointer'
          width={22}
          height={22}
        />
      )
    }
    return (
      <EyeSlashIcon
        onClick={toggleComponent}
        className='cursor-pointer'
        width={22}
        height={22}
      />
    )
  }

  const numberFormatProps = { ...props }
  delete numberFormatProps.maskedFormat
  delete numberFormatProps.setFieldValue
  delete numberFormatProps.style

  if (isLoading)
    return (
      <div className='w-full flex justify-center items-center'>
        <LoadingIcon />
      </div>
    )

  return (
    <div className={numberFormatProps.parentClassName}>
      {!!label && (
        <label className='text-sm text-gray-400 flex mb-2'>
          {label}
          {labelFlag}
        </label>
      )}
      <div className='relative'>
        <NumberFormat
          {...numberFormatProps}
          value={field.value}
          onChange={() => setIsMasked(false)}
          className={`${fieldClasses} ${numberFormatProps.customClassName}`}
          onValueChange={(values) => {
            const value = values.formattedValue.replaceAll('-', '')
            helpers.setValue(value, true)
            if (afterOnChange) afterOnChange()
          }}
        />
        <div className={`absolute ${numberFormatProps.customRight || 'right-2.5'} top-2.5`}>
          {displayEyeIcon()}
        </div>
      </div>
      {showError && !!getIn(errors, name) && !!getIn(touched, name) && (
        <div className='text-error'>{getIn(errors, name)}</div>
      )}
    </div>
  )
}

export default MaskedSSNNUmberField
